/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import { storyblokEditable } from '@storyblok/react/rsc'
import deepmerge from 'deepmerge'
import React from 'react'
import {
  MARK_BOLD,
  MARK_CODE,
  MARK_ITALIC,
  MARK_LINK,
  MARK_STRIKE,
  MARK_STYLED,
  MARK_TEXT_STYLE,
  MARK_UNDERLINE,
  NODE_HEADING,
  NODE_HR,
  NODE_LI,
  NODE_OL,
  NODE_PARAGRAPH,
  NODE_QUOTE,
  NODE_UL,
  RenderOptions,
  StoryblokRichtext,
} from 'storyblok-rich-text-react-renderer'
import { RichTextRenderer } from '@/components/storyblok/components/ui/RichtextRenderer'
import { ManualBlok } from '@/components/storyblok/manual-bloks'
import { Link } from '@/i18n/navigation'

/** A description of the available properties for this component. */
type ManualRichTextRendererProps = {
  /** The Storyblok rich text document to render. */
  document: StoryblokRichtext | unknown

  /** Additional options to pass to the renderer. */
  options?: RenderOptions
}

/**
 * A wrapper around the custom Storyblok richtext renderer we use for the website, though this has more specific rule
 * overrides for use within the manuals specifically.
 */
const ManualRichTextRenderer: React.FC<ManualRichTextRendererProps> = ({ document, options }) => {
  const defaultOptions: RenderOptions = {
    nodeResolvers: {
      [NODE_PARAGRAPH]: (children) => <p className={`text-base py-1`}>{children}</p>,
      [NODE_HEADING]: (children, { level }) => {
        const HeadingTag = `h${level}` as keyof React.JSX.IntrinsicElements
        const sizeClasses = {
          1: 'text-2xl',
          2: 'text-xl',
          3: 'text-lg',
          4: 'text-base',
          5: 'text-sm',
          6: 'text-xs',
        }
        return <HeadingTag className={`font-semibold mb-2 last:mb-0  ${sizeClasses[level]}`}>{children}</HeadingTag>
      },
      [NODE_OL]: (children) => <ol className={`list-decimal pl-8 my-2`}>{children}</ol>,
      [NODE_UL]: (children) => (
        <ul className={`list-disc list-inside marker:text-teal-600 marker:text-xl space-y-0.5`}>{children}</ul>
      ),
      [NODE_LI]: (children) => <li className={'leading-relaxed [&>p]:inline'}>{children}</li>,
      [NODE_QUOTE]: (children) => (
        <blockquote className={`border-l-4 border-gray-300 pl-4 italic mb-4`}>{children}</blockquote>
      ),
      [NODE_HR]: () => <div className={'block py-1'} />,
    },
    markResolvers: {
      [MARK_BOLD]: (children) => <strong className={'font-bold'}>{children}</strong>,
      [MARK_ITALIC]: (children) => <em className={'italic'}>{children}</em>,
      [MARK_STRIKE]: (children) => <span className={'line-through'}>{children}</span>,
      [MARK_UNDERLINE]: (children) => <span className={'underline'}>{children}</span>,
      [MARK_CODE]: (children) => <code className={'bg-gray-100 rounded-xs px-1 py-0.5'}>{children}</code>,
      [MARK_STYLED]: (children, { class: className }) => <span className={className || ''}>{children}</span>,
      [MARK_TEXT_STYLE]: (children, { color }) => <span style={{ color }}>{children}</span>,
      [MARK_LINK]: (children, { href, target }) => (
        <Link href={href!} target={target} className={'text-blue-600 hover:underline'}>
          {children}
        </Link>
      ),
    },
    defaultStringResolver: (string) => <>{string}</>,
    textResolver: (string) => string,
    defaultBlokResolver: (name, props) => {
      const blok = { ...props, component: name }
      // Resolve via the manual override map (not the global registry) so manual variants of swapped bloks render
      // deterministically in every realm — see manual-bloks.tsx.
      return <ManualBlok blok={blok} key={props._uid} {...storyblokEditable(blok)} />
    },
  }

  return (
    <RichTextRenderer document={document} options={options ? deepmerge(defaultOptions, options) : defaultOptions} />
  )
}

export { ManualRichTextRenderer }
